import { Bot } from 'lucide-react';
import { useId, type ChangeEvent } from 'react';
import { LiksPhotoUpload } from '../../../components/liks/LiksPhotoUpload/LiksPhotoUpload';
import { useI18n } from '../../../i18n/useI18n';
import {
  DEFAULT_CHATBOT_PRIMARY_COLOR,
  type ChatbotPlacement,
} from '../../../types/chatbot';
import {
  PLACEMENT_OPTIONS,
  placementI18nKey,
} from './chatbotConfigurationHelpers';
import { ChatbotSettingsCardFooter } from './ChatbotSettingsCardFooter';
import './ChatbotAppearanceCard.css';

type ChatbotAppearanceCardProps = {
  logoDisplaySrc: string | null;
  isLogoSaving: boolean;
  logoError: string | null;
  displayName: string;
  primaryColor: string;
  welcomeMessage: string;
  placement: ChatbotPlacement;
  isDirty: boolean;
  isSaving: boolean;
  isSaveDisabled: boolean;
  discardLabel: string;
  saveLabel: string;
  onLogoChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onRemoveLogo: () => void;
  onDisplayNameChange: (value: string) => void;
  onPrimaryColorChange: (value: string) => void;
  onWelcomeMessageChange: (value: string) => void;
  onPlacementChange: (value: ChatbotPlacement) => void;
  onDiscard: () => void;
  onSave: () => void;
};

export function ChatbotAppearanceCard({
  logoDisplaySrc,
  isLogoSaving,
  logoError,
  displayName,
  primaryColor,
  welcomeMessage,
  placement,
  isDirty,
  isSaving,
  isSaveDisabled,
  discardLabel,
  saveLabel,
  onLogoChange,
  onRemoveLogo,
  onDisplayNameChange,
  onPrimaryColorChange,
  onWelcomeMessageChange,
  onPlacementChange,
  onDiscard,
  onSave,
}: ChatbotAppearanceCardProps) {
  const { translate } = useI18n();
  const logoFieldId = useId();

  return (
    <div className="chatbot-settings__card">
      <div className="chatbot-settings__body">
        <h2 className="chatbot-settings__title">
          {translate('chatbots.settings.appearance.title')}
        </h2>

        <LiksPhotoUpload
          className="chatbot-settings__logo"
          variant="tenant"
          label={translate('chatbots.settings.appearance.logoLabel')}
          src={logoDisplaySrc}
          isLoading={isLogoSaving}
          placeholder={<Bot strokeWidth={1.5} aria-hidden />}
          inputId={logoFieldId}
          disabled={isLogoSaving || isSaving}
          onFileChange={(event) => {
            void onLogoChange(event);
          }}
          onRemove={onRemoveLogo}
          error={logoError}
        />

        <div className="chatbot-settings__field">
          <p className="chatbot-settings__label">
            {translate('chatbots.settings.appearance.displayNameLabel')}
          </p>
          <input
            className="chatbot-settings__origin-input"
            value={displayName}
            onChange={(event) => onDisplayNameChange(event.target.value)}
            placeholder={translate(
              'chatbots.settings.appearance.displayNamePlaceholder',
            )}
            disabled={isSaving}
            aria-label={translate(
              'chatbots.settings.appearance.displayNameLabel',
            )}
          />
        </div>

        <div className="chatbot-settings__field">
          <p className="chatbot-settings__label">
            {translate('chatbots.settings.appearance.colorLabel')}
          </p>
          <div className="chatbot-settings__color-row">
            <input
              type="color"
              className="chatbot-settings__color-swatch"
              value={
                /^#[0-9a-fA-F]{6}$/.test(primaryColor)
                  ? primaryColor
                  : DEFAULT_CHATBOT_PRIMARY_COLOR
              }
              onChange={(event) => onPrimaryColorChange(event.target.value)}
              aria-label={translate('chatbots.settings.appearance.colorLabel')}
              disabled={isSaving}
            />
            <input
              className="chatbot-settings__origin-input"
              value={primaryColor}
              onChange={(event) => onPrimaryColorChange(event.target.value)}
              placeholder={translate(
                'chatbots.settings.appearance.colorPlaceholder',
              )}
              disabled={isSaving}
            />
          </div>
        </div>

        <div className="chatbot-settings__field">
          <p className="chatbot-settings__label">
            {translate('chatbots.settings.appearance.welcomeLabel')}
          </p>
          <textarea
            className="chatbot-settings__textarea"
            value={welcomeMessage}
            onChange={(event) => onWelcomeMessageChange(event.target.value)}
            placeholder={translate(
              'chatbots.settings.appearance.welcomePlaceholder',
            )}
            rows={4}
            disabled={isSaving}
            aria-label={translate('chatbots.settings.appearance.welcomeLabel')}
          />
        </div>

        <div className="chatbot-settings__field">
          <p className="chatbot-settings__label">
            {translate('chatbots.settings.appearance.placementLabel')}
          </p>
          <div className="chatbot-settings__placement-grid">
            {PLACEMENT_OPTIONS.map((option) => (
              <button
                key={option}
                type="button"
                className={[
                  'chatbot-settings__placement-button',
                  placement === option
                    ? 'chatbot-settings__placement-button--active'
                    : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                disabled={isSaving}
                onClick={() => onPlacementChange(option)}
              >
                {translate(
                  `chatbots.settings.placement.${placementI18nKey(option)}`,
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      <ChatbotSettingsCardFooter
        isDirty={isDirty}
        isSaving={isSaving || isLogoSaving}
        isSaveDisabled={isSaveDisabled}
        discardLabel={discardLabel}
        saveLabel={saveLabel}
        onDiscard={onDiscard}
        onSave={onSave}
      />
    </div>
  );
}
